import React from 'react';
import { Alert } from '@mantine/core';
import { useSignalR } from "@/signalr/providers/SignalRClientProvider.tsx";
import { IconAlertTriangle, IconBroadcastOff } from "@tabler/icons-react";

/**
 * Displays a warning banner when SignalR connection has been unhealthy for an extended period of time.
 * Only shows when there's a sustained problem that could result in missed real-time notifications.
 */
export const ConnectionStatusIndicator: React.FC = () => {
  const {hasPersistedError, error, currentSystemId} = useSignalR();

  // Only show when error has persisted for 10+ seconds
  if (!hasPersistedError) {
    return null;
  }

  // Determine if we're actively reconnecting
  const isReconnecting = error?.message?.startsWith('Reconnecting');

  // Create context-aware message
  const systemContext = currentSystemId ? ` for system ${currentSystemId}` : '';

  return (
    <Alert variant="light"
           color={isReconnecting ? 'orange' : 'red'}
           icon={isReconnecting ? <IconAlertTriangle size={16}/> : <IconBroadcastOff size={16}/>}
           title={isReconnecting ? 'Reconnecting to real-time updates...' : 'Real-time updates unavailable'}
           aria-label="signalr-connection-status-alert"
           mb="md">
      {isReconnecting
        ? `Connection is being restored. You may miss real-time updates${systemContext}. Try refreshing the page if this persists.`
        : `Connection lost. You will not receive real-time updates${systemContext}. Try refreshing the page to restore the connection.`}
    </Alert>
  );
};